import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
  useTheme
} from '@mui/material';
import { useNoticeStore } from 'store/useNoticeStore';
import dayjs from 'dayjs';
import Tiptap from 'components/Tiptap/Tiptap';
import { deleteNoticesIdAPI, getNoticesIdAPI } from 'apis/notice/noticeAPI';
import { useTranslation } from 'react-i18next';
import { useSnackbarStore } from 'store/useSnackbarStore';
import SnackbarAlert from 'components/SnackbarAlert';
import useUserStore from 'store/useUserStore';

// 공지사항 상세 데이터 타입
export interface NoticeDetail {
  id: number; // 공지사항 ID
  title: string; // 제목
  content: string; // 내용
  language: string; // 언어 코드 (예: 'ko')
  isFixed: boolean; // 상단 고정 여부
  isPopup: boolean; // 팝업 공지 여부
  isHidden: boolean; // 숨김 여부
  createdAt: string; // 생성일 (ISO-8601 형식)
  updatedAt: string; // 수정일 (ISO-8601 형식)
  createdBy: number | null; // 생성자 ID (nullable)
  updatedBy: number | null; // 수정자 ID (nullable)
  viewCount: number; // 조회수
  attachments: Attachment[] | null; // 첨부 파일 (nullable)
  popupExpiredAt: string | null; // 팝업 만료일 (nullable)
}

// 첨부 파일 타입 (예시, 실제 데이터 구조에 따라 수정 가능)
export interface Attachment {
  id: number; // 첨부 파일 ID
  fileName: string; // 파일 이름
  fileUrl: string; // 파일 URL
}

// 공지사항 상세 컴포넌트
export default function NoticeDetail() {
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  const [notice, setNotice] = useState<NoticeDetail | null>(null); // 공지사항 데이터 상태
  const [loading, setLoading] = useState<boolean>(true); // 로딩 상태
  const [error, setError] = useState<string | null>(null); // 에러 상태
  const [prevLanguage, setPrevLanguage] = useState(i18n.language); // 언어 변화 감지 상태
  const selectedNotice = useNoticeStore((state) => state.selectedNotice); // 선택한 공지사항 번호
  const contentEditorRef = useRef<any>(null);
  const { id } = useParams<{ id: string }>();
  // 스낵바
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const [isAdmin, setIsAdmin] = useState(false); // 관리자 여부 (팝업 Chip에 사용됨)
  const { user } = useUserStore(); // 로그인 한 사람의 정보 (isAdmin에 사용됨)
  const navigate = useNavigate();
  // 다크 라이트 모드
  const theme = useTheme();
  // 컴포넌트 내부 상태와 이벤트 핸들러 추가
  const [confirmPopupOpen, setConfirmPopupOpen] = useState(false);
  const [dialogText, setDialogText] = useState<string | null>(null);

  // 날짜 포맷팅
  const formattedDate = dayjs(notice?.createdAt).format('YYYY년 MM월 DD일 HH:mm');

  // 데이터 fetch 함수
  const fetchNotice = async (noticeId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getNoticesIdAPI(noticeId);
      setNotice(response.data);
    } catch (err) {
      showSnackbar(t('notice.detail.data_error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // id가 변경될 때마다 fetchNotice 호출
  useEffect(() => {
    fetchNotice(id as unknown as number);

    if (user?.groups.some((group) => group.type === 'SYSTEM_ADMIN')) {
      setIsAdmin(true);
    }
  }, [id]);

  // 언어가 바뀌면, 목록으로 이동
  useEffect(() => {
    if (prevLanguage !== i18n.language) {
      navigate('/notice');
      setPrevLanguage(i18n.language);
    }
  }, [i18n.language, prevLanguage, navigate]);

  // 로딩 상태 처리
  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }
  // 에러 상태 처리
  if (error) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Paper elevation={3} style={{ padding: '20px', textAlign: 'center', width: '400px' }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // 이벤트 핸들러
  const handleConfirmPopupOpen = (text: string) => {
    setDialogText(text);
    setConfirmPopupOpen(true);
  };

  const handleConfirmPopupClose = () => {
    setDialogText(null);
    setConfirmPopupOpen(false);
  };

  const handleConfirm = async () => {
    if (dialogText === '삭제' && notice) {
      try {
        await deleteNoticesIdAPI(notice.id); // 삭제 API 호출
        navigate('/notice');
        showSnackbar(t('notice.detail.delete_success'), 'success');
      } catch (err) {
        showSnackbar(t('notice.detail.delete_error'), 'error');
      }
    }
    handleConfirmPopupClose();
  };

  return (
    <Box p={4} style={{ width: '100%' }}>
      <SnackbarAlert />
      {!notice ? (
        <Typography variant="h6" color="textSecondary" textAlign="center">
          {t('notice.detail.no_notice')}
        </Typography>
      ) : (
        <>
          {/* 제목 섹션 */}
          <Box mb={3}>
            {/* 첫 번째 행: 제목과 공지 상태 */}
            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={1}
              sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'start' } }}
            >
              {/* 제목 */}
              <Typography variant="h4" gutterBottom style={{ fontWeight: 'bold' }}>
                {notice.title}
              </Typography>

              {/* 공지 상태 */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                {selectedNotice?.isAdmin && notice.isFixed && (
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: '#ffeb3b',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      color: '#333',
                      marginBottom: { xs: '8px', sm: '0' },
                      marginLeft: { xs: '0', sm: '8px' }
                    }}
                  >
                    {t('notice.detail.fixed')}
                  </Typography>
                )}
                {selectedNotice?.isAdmin && notice.isPopup && (
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: '#ffeb3b',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      color: '#333',
                      marginLeft: { xs: '0', sm: '8px' }
                    }}
                  >
                    {t('notice.detail.pop_up')}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* 두 번째 행: 작성일과 조회수 */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {/* 작성일 */}
              <Typography variant="caption" color="textSecondary">
                {t('notice.detail.date')}: {formattedDate}
              </Typography>
              {/* 조회수 */}
              <Typography variant="caption" color="textSecondary">
                {t('notice.detail.count')}: {notice.viewCount}
              </Typography>
            </Box>
          </Box>

          <Divider style={{ margin: '16px 0' }} />

          {/* 내용 섹션 */}
          <Box mb={3}>
            <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.mode == 'dark' ? theme.palette.contentBlock.background : 'auto'}>
              <Tiptap ref={contentEditorRef} content={notice.content} editable={false} minHeight="400px" maxHeight="calc(100vh - 300px)" />
            </Box>
          </Box>

          {/* 수정 및 삭제 버튼 */}
          {isAdmin && (
            <Box display="flex" justifyContent="flex-start" gap={2}>
              <Button variant="contained" color="primary" onClick={() => navigate(`/notice/edit/${notice.id}`)} sx={{ borderRadius: '4px' }}>
                {t('notice.detail.edit')}
              </Button>
              <Button variant="contained" color="error" onClick={() => handleConfirmPopupOpen(t('notice.detail.delete'))} sx={{ borderRadius: '4px' }}>
                {t('notice.detail.delete')}
              </Button>
            </Box>
          )}

          {/* 컨펌 다이얼로그 */}
          {confirmPopupOpen && dialogText && (
            <Dialog open={confirmPopupOpen} onClose={handleConfirmPopupClose}>
              <DialogTitle>{`${dialogText} 하시겠습니까?`}</DialogTitle>

              <DialogActions sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={handleConfirmPopupClose} color="inherit">
                    {t('notice.detail.cancel')}
                  </Button>
                  <Button onClick={handleConfirm} color="primary">
                    {t('notice.detail.ok')}
                  </Button>
                </Box>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}
    </Box>
  );
}
